import { useTheme } from "next-themes";
import { Button } from "./ui/button";
import { useEffect } from "react";
interface PrivacyPolicyProps {
  onNavigate: (page: string) => void;
}

export function PrivacyPolicy({ onNavigate }: PrivacyPolicyProps) {
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }, []);
  const { theme } = useTheme();
  const logoSrc = theme === "dark" ? "/logo_white.png" : "/logo.png";

  const navigate = (url: string, page: string) => {
    window.history.pushState({}, "", url);
    onNavigate(page);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* HEADER */}
      <header className="border-b sticky top-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/", "landing")}
            className="flex items-center"
          >
            <img src={logoSrc} alt="Swimnerd Logo" className="h-10 w-auto" />
          </button>

          <nav className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate("/find-pool", "find-pool")}>
              Find Pool
            </Button>
            <Button variant="ghost" onClick={() => navigate("/blog", "blog")}>
              Blog
            </Button>
            <Button variant="ghost" onClick={() => navigate("/signin", "signin")}>
              Sign In
            </Button>
            <Button onClick={() => navigate("/signup", "signup")}>
              Get Started
            </Button>
          </nav>
        </div>
      </header>

      {/* HERO (same style as Blog / Find Pool) */}
      <section className="relative overflow-hidden text-white">
        <div className="absolute inset-0 bg-[linear-gradient(135deg,hsl(var(--primary)),hsl(var(--primary)/0.85))]" />
        <div className="relative max-w-7xl mx-auto px-6 py-20">
          <h1 className="text-5xl font-bold mb-6 max-w-3xl">
            Privacy Policy
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Learn how Swimnerd collects, uses, and protects your information.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <main className="flex-1 bg-background">
        <div className="max-w-4xl mx-auto px-6 py-20">
          <p className="text-muted-foreground mb-8">
            Last updated: January 2026
          </p>

          <section className="space-y-8 text-gray-700 dark:text-gray-300 leading-8">
            <p>
              At Swimnerd, your privacy is important to us. This Privacy Policy
              explains how we collect, use, and protect your information when
              you use our platform.
            </p>

            <h2 className="text-2xl font-semibold">Information We Collect</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Personal details such as name, email, and profile data</li>
              <li>Usage data including pages visited and interactions</li>
              <li>Payment and subscription information (securely processed)</li>
            </ul>

            <h2 className="text-2xl font-semibold">How We Use Information</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide and improve Swimnerd services</li>
              <li>To personalize training and content</li>
              <li>To process payments and subscriptions</li>
              <li>To communicate updates and support messages</li>
            </ul>

            <h2 className="text-2xl font-semibold">Data Protection</h2>
            <p>
              We use industry-standard security practices to protect your data.
              Sensitive information is encrypted and access is strictly
              controlled.
            </p>

            <h2 className="text-2xl font-semibold">Third-Party Services</h2>
            <p>
              Swimnerd may use trusted third-party services for analytics,
              payments, and messaging. These providers comply with applicable
              data protection regulations.
            </p>

            <h2 className="text-2xl font-semibold">Your Rights</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access and update your personal information</li>
              <li>Request data deletion</li>
              <li>Opt out of non-essential communications</li>
            </ul>

            <h2 className="text-2xl font-semibold">Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy, please contact
              us at <strong>support@swimnerd.com</strong>.
            </p>
          </section>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="border-t text-center py-8 text-sm text-muted-foreground">
        © {new Date().getFullYear()} Swimnerd. Built for smarter swimming.
      </footer>
    </div>
  );
}
